import {AfterContentInit, Component, OnDestroy, OnInit, ViewChild}           from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of, Subscription}        from 'rxjs';
import {map, skip}                                                           from 'rxjs/operators';
import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                                                            from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  TaskManagementDataProviders
}                                                                            from '../../task-management-data-providers/task-management-data-providers';
import {FieldStore}                                                          from '../../../stores/farm/field.store';
import {
  ApUtilService
}                                                                            from '../../../ap-utils/service/ap-util.service';
import {
  ApDynGridStatusColumnConfig
}                                                                            from '../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                                                            from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridGroupColumnConfig
}                                                                            from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {
  ApDynGridEditColumnConfig
}                                                                            from '../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApDynGridPagerConfigBase
}                                                                            from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynGridPagerConfig,
  IDynGridWizardChoiceData
}                                                                            from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {ApGridPagerType}                                                     from '../../../ap-interface';
import {FormStore}                                                           from '../../../stores/layout/form.store';
import {
  ModalDialogPresets
}                                                                            from '../../../stores/dialog/modal.dialog.presets';
import {
  ModalDialogStore
}                                                                            from '../../../stores/dialog/modal.dialog.store';
import {IRouterStateUrl}                                                     from '../../../stores/router/router.store';
import {
  ApDynComponentComponent
}                                                                            from '../../../ap-dyncomponent/ap-dyncomponent.component';
import {MapStore}                                                            from '../../../stores/map/map.store';
import {
  LanguageStore
}                                                                            from '../../../stores/translation/language.store';
import {DriverStore}                                                         from '../../../stores/docu/driver.store';
import {MachineStore}                                                        from '../../../stores/docu/machine.store';
import {
  InstrumentStore
}                                                                            from '../../../stores/docu/instrument.store';
import {ITaskManagementPlanBookGridData, ITaskManagementPlanSummaryGridData} from '../../task-management.types';
import {
  CropRotationStore
}                                                                            from '../../../stores/farm/crop.rotation.store';
import {
  FieldArchiveStore
}                                                                            from '../../../stores/farm/field-archive.store';
import {nameOf}                                                              from '../../../ap-core/utils/ap-name-of';
import {
  IGridStatus
}                                                                            from '../../../ap-interface/interfaces/ap-grid-status.interface';
import ILanguage = Data.Language.ILanguage;
import TaskManagementModule = Data.TaskManagement.TaskManagementModule;
import ITaskManagementPlanBook = Data.TaskManagement.ITaskManagementPlanBook;
import ITaskManagementPlanSummary = Data.TaskManagement.ITaskManagementPlanSummary;
import IFieldBase = Data.FieldManagement.IFieldBase;
import IMachine = Data.DocuContext.Machine.IMachine;
import IInstrument = Data.DocuContext.Instrument.IInstrument;
import IDriver = Data.DocuContext.Driver.IDriver;

@Component({
  selector: 'task-management-edit',
  template: `
    <ap-dyncomponent [caption]="'Global__Show'"
                     [headerIcon]="'ap-icon-tasks'"
                     [filterTooltip]="''"
                     [loading$]="loading$"
                     [canSearch]="true"
                     [canCreate]="false"
                     [columns]="columns$ | async"
                     [items]="items$ | async"
                     [selectable]="{checkboxOnly: false, mode: 'multiple', enabled: true}"
                     [pager]="pager">
    </ap-dyncomponent>
  `
})
export class TaskManagementEditComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  public data: { PlanSummary: ITaskManagementPlanSummaryGridData, RouterData?: IRouterStateUrl };
  public loading$: Observable<boolean> = of(true);
  public items$ = new BehaviorSubject<ITaskManagementPlanBookGridData[]>([]);
  public columns$: Observable<ApDynGridColumnConfigBase[]>;
  public pager: ApDynGridPagerConfigBase;

  private _subscriptions: Subscription[] = [];
  private _planBooks: ITaskManagementPlanBook[];
  private _planSummary: ITaskManagementPlanSummary;
  private _itemsToDelete: ITaskManagementPlanBook[];
  private _isDeleteAvailable = true;

  constructor(private dataProviders: TaskManagementDataProviders,
              private fieldStore: FieldStore,
              private formStore: FormStore,
              private mapStore: MapStore,
              private cropRotationStore: CropRotationStore,
              private modalDialogPresets: ModalDialogPresets,
              private modalDialogStore: ModalDialogStore,
              private languageStore: LanguageStore,
              private driverStore: DriverStore,
              private machineStore: MachineStore,
              private fieldArchiveStore: FieldArchiveStore,
              private instrumentStore: InstrumentStore) {
  }

  public ngOnInit(): void {
    this._planSummary = this.data?.PlanSummary?.SourceItem;
    this._planBooks = this.data?.PlanSummary?.SourceItem.PlanBooks;
    if (!this._planBooks || this._planBooks?.length === 0) {
      this._closeForm();
      return;
    }
    this.loading$ = combineLatest([
      this.cropRotationStore.Listen(x => x.historyLoading),
      this.languageStore.Listen(x => x.loading)
    ]).pipe(
      map(loadingItems => loadingItems.some(x => x))
    );
    this.columns$ = this._getColumns();
    this.pager = this._getPager();
    this.dataProviders[this._planSummary.Module].setSelectedGridIdFromPreviousPage(this._planSummary.Id);
    this._activateTaskManagementMapLayer();
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(this.dynComponent.gridInitialized.subscribe(() => {
      this._subscriptions.push(this.items$.subscribe(items => {
        const ids = items.map(x => x.Id);
        setTimeout(() => this.dynComponent.dynGrid.gridPaging.setSelectedKeys(ids), 1);
      }));
      this._subscriptions.push(this.dynComponent.SelectedItems$.subscribe(_ => this._selectionChange()));
    }));
    this._subscriptions.push(this.dataProviders[this._planSummary.Module].changesSaveStatus().subscribe(saved => {
      this._isDeleteAvailable = true;
      if (!saved || !this._itemsToDelete || !this._planBooks) {
        return;
      }
      this._planBooks = this._planBooks.filter(planBook => !this._itemsToDelete.some(z => z.Id === planBook.Id));
      this._itemsToDelete = [];
      if (this._planBooks.length > 0) {
        this.items$.next(this._getItems(this._planBooks));
      } else {
        this._closeForm();
      }
    }));
    this._subscriptions.push(this.cropRotationStore.GetHistory$.subscribe(() => {
      this.items$.next(this._getItems(this._planBooks));
    }));
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  private _getColumns(): Observable<ApDynGridColumnConfigBase[]> {
    return combineLatest([
      this.languageStore.Listen(x => x.selectedLanguage)
    ]).pipe(
      map(([selectedLanguage]) => {
        const columns: ApDynGridColumnConfigBase[] = [];
        if (this._planSummary.Module === TaskManagementModule.GD_BasicFertilization) {
          columns.AddRange(this._getCommonBeginningGridColumnConfig());
          columns.AddRange(this._getBaseFertiGridColumnConfig());
          columns.AddRange(this._getCommonEndingGridColumnConfig(selectedLanguage));
          if (this._isBaseFertilisationPlanning()) {
            columns.AddRange(this._getBaseFertiGridEditColumnConfig());
          }
        } else if (this._planSummary.Module === TaskManagementModule.N_NFertilization) {
          columns.AddRange(this._getCommonBeginningGridColumnConfig());
          columns.AddRange(this._getNFertiGridColumnConfig());
          columns.AddRange(this._getCommonEndingGridColumnConfig(selectedLanguage));
        } else {
          columns.AddRange(this._getCommonBeginningGridColumnConfig());
          columns.AddRange(this._getCommonEndingGridColumnConfig(selectedLanguage));
        }
        return columns;
      })
    );
  }

  private _getPager(): ApDynGridPagerConfig {
    const listMassEditingSubject: BehaviorSubject<IDynGridWizardChoiceData[]> = new BehaviorSubject([]);
    const listMassEditingObservable: Observable<IDynGridWizardChoiceData[]> = listMassEditingSubject.asObservable();
    const pager = new ApDynGridPagerConfig({
      lowerButtons: [
        {
          name: 'Global_Back_With_Arrow',
          callback: ApUtilService.createEventEmitter(this._closeForm.bind(this), this._subscriptions)
        }
      ],
      listMassEditing: listMassEditingObservable
    });
    if (this._isBaseFertilisationPlanning()) {
      listMassEditingSubject.next([
        {
          id: 0,
          name: 'Global__Delete',
          type: ApGridPagerType.None,
          onSubmit: ApUtilService.createEventEmitter(this._deleteManyPlanningBooks.bind(this), this._subscriptions)
        }
      ]);
    }
    return pager;
  }

  private _getItems(planBooks: ITaskManagementPlanBook[]): ITaskManagementPlanBookGridData[] {
    return planBooks.map(planBook => {
      const field = this.fieldStore.Fields.find(f => f.Id === planBook.FieldId) ??
        this.fieldArchiveStore.getFieldArchiveById(planBook.FieldId);
      return {PlanBook: planBook, Field: field};
    }).map<ITaskManagementPlanBookGridData>(fieldPlanBook => {
      const planBook = fieldPlanBook.PlanBook;
      const field = fieldPlanBook.Field;
      return {
        Id: planBook.Id,
        Field: field,
        Status: this.dataProviders[this._planSummary.Module].getStatus(planBook),
        ModuleText: this.dataProviders[this._planSummary.Module].getModuleText(planBook),
        ApplicationModeText: this.dataProviders[this._planSummary.Module].getApplicationModeText(planBook),
        OperationModeText: this.dataProviders[this._planSummary.Module].getOperationModeText(planBook),
        ElementName: this.dataProviders[this._planSummary.Module].getElementName(planBook),
        ProductName: this.dataProviders[this._planSummary.Module].getProductName(planBook),
        ProductUnitPerHa: this.dataProviders[this._planSummary.Module].getProductUnit(planBook, true),
        CropName: this.dataProviders[this._planSummary.Module].getCropName(planBook),
        AreaHa: this.dataProviders[this._planSummary.Module].getAreaHaSum(planBook),
        ApplRate: this.dataProviders[this._planSummary.Module].getApplRate(planBook),
        ApplMin: this.dataProviders[this._planSummary.Module].getApplMin(planBook),
        ApplMax: this.dataProviders[this._planSummary.Module].getApplMax(planBook),
        ApplSum: this.dataProviders[this._planSummary.Module].getApplSum(planBook),
        ApplDate: this.dataProviders[this._planSummary.Module].getDate(planBook.ApplDate),
        ExportDriver: this.driverStore.getDriverById(fieldPlanBook?.PlanBook?.ExportDriver?.Id),
        ExportMachine: this.machineStore.getMachineById(fieldPlanBook?.PlanBook?.ExportMachine?.Id),
        ExportInstrument: this.instrumentStore.getInstrumentById(fieldPlanBook?.PlanBook?.ExportInstrument?.Id),
        SelectedExportTargetObject: undefined,
        SourceItem: planBook
      } as ITaskManagementPlanBookGridData;
    });
  }

  private _selectionChange(): void {
    if (!this.dynComponent?.dynGrid?.gridPaging) {
      return;
    }
    const selectedPlanBooks = this.dynComponent.dynGrid.gridPaging.selectedItems as ITaskManagementPlanBookGridData[];
    if (selectedPlanBooks && selectedPlanBooks.length > 0) {
      const selectedPlans = this._planBooks
        .filter(sourceItemPlanBook => selectedPlanBooks.some(selectedPlanBook => selectedPlanBook.Id === sourceItemPlanBook.Id));
      this.fieldStore.changeSelectedField(selectedPlans.map(p => p.FieldId?.toString()));
      this.mapStore.Layers.TaskManagementLayer.replaceSource(selectedPlans, this.fieldStore);
      this.mapStore.Layers.TaskManagementLayer.ZoomIn(500);
      const dataProvider = this.dataProviders[this._planSummary.Module];
      const mapUrl = dataProvider?.getMapFactoryUrl(selectedPlans);
      if (mapUrl?.length > 0) {
        const legend = dataProvider?.getMapLegend(this._planSummary)();
        this.mapStore.Legends.TaskMgmtLegend = legend;
        this.mapStore.Legends.refreshLegend(legend, true);
        this.mapStore.Layers.TaskManagementNutrientLayer.Legend = () => legend;
        this.mapStore.Layers.TaskManagementNutrientLayer.update(mapUrl);
      } else {
        this.mapStore.Layers.TaskManagementNutrientLayer.clear();
      }
      setTimeout(() => dataProvider?.selectLegendItem(this._planSummary), 0);
    } else {
      this.mapStore.Layers.TaskManagementLayer.clear();
      this.mapStore.Layers.TaskManagementNutrientLayer.clear();
      this.fieldStore.changeSelectedField([]);
    }
  }

  private _isBaseFertilisationPlanning(): boolean {
    return this.data?.RouterData?.queryParams['url'] === this.dataProviders[TaskManagementModule.GD_BasicFertilization].getComponentUrl()
      && this.data.RouterData.queryParams['booking'] === undefined;
  }

  private _activateTaskManagementMapLayer(): void {
    this.mapStore.Layers.TaskManagementLayer.Visibility = true;
    this.mapStore.Layers.refreshActiveLayerGroups(this.mapStore.Layers.TaskManagementLayer).then();
    this.mapStore.Legends.refreshLegend(this.mapStore.Legends.TaskMgmtLegend, true);
  }

  private _editPlanningBook(bookPlanningToEdit: any): void {
    if (!this._planSummary || !bookPlanningToEdit) {
      return;
    }
  }

  private _deletePlanningBook(bookPlanningToDelete: ITaskManagementPlanBookGridData): void {
    if (!this._isDeleteAvailable || !this._planSummary || !bookPlanningToDelete) {
      return;
    }
    this.modalDialogStore.setModalDialogData(this.modalDialogPresets.DeleteDialog());
    const modalDialogSubscription = this.modalDialogStore.Listen(s => s.result)
      .pipe(skip(1))
      .subscribe((result) => {
        modalDialogSubscription.unsubscribe();
        if (result.key === 'delete') {
          this._deleteItems([bookPlanningToDelete]);
        }
      });
  }

  private _deleteManyPlanningBooks(bookPlanningsToDelete: any): void {
    if (!this._isDeleteAvailable || !this._planSummary || !bookPlanningsToDelete?.items) {
      return;
    }
    this._deleteItems(bookPlanningsToDelete.items);
  }

  private _deleteItems(items: ITaskManagementPlanBookGridData[]): void {
    this._itemsToDelete = this._planBooks.filter(x => items.some(bookPlanToDelete => x.Id === bookPlanToDelete.Id));
    if (this._itemsToDelete) {
      this._itemsToDelete.forEach(item => {
        if (!item.Summary) {
          item.Summary = {
            Id: this._planSummary.Id
          } as any;
        }
      });
      this._isDeleteAvailable = false;
      this.dataProviders[this._planSummary.Module].deletePlannings(this._itemsToDelete);
    }
  }

  private _closeForm(): void {
    setTimeout(() => this.formStore.closeForm(), 1);
  }

  /*
   * Columns which are same for all views at the beginning of the view
   */
  private _getCommonBeginningGridColumnConfig(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridStatusColumnConfig({
        field: `${nameOf<ITaskManagementPlanBookGridData>('Status')}.${nameOf<IGridStatus>('state')}`,
        class: `${nameOf<ITaskManagementPlanBookGridData>('Status')}.${nameOf<IGridStatus>('class')}`,
        description: `${nameOf<ITaskManagementPlanBookGridData>('Status')}.${nameOf<IGridStatus>('description')}`
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Number',
        field: `${nameOf<ITaskManagementPlanBookGridData>('Field')}.${nameOf<IFieldBase>('FieldNumber')}`,
        filterable: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        headerFilterable: true,
        width: 50
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini_Subnumber',
        field: `${nameOf<ITaskManagementPlanBookGridData>('Field')}.${nameOf<IFieldBase>('FieldSubnumber')}`,
        filterable: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        headerFilterable: true,
        width: 50
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__FieldName',
        field: `${nameOf<ITaskManagementPlanBookGridData>('Field')}.${nameOf<IFieldBase>('FieldName')}`,
        filterable: true,
        headerFilterable: true,
        sortIndex: 0,
        sortDesc: false,
        width: 160,
        cssClass: 'left',
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Area_Unit',
        field: nameOf<ITaskManagementPlanBookGridData>('AreaHa'),
        cssClass: 'right',
        headerFilterable: true,
        filterable: true,
        width: 60,
        tooltip: (dataItem) => dataItem['AreaOriginal'],
        hide: new ApDynGridColumnHideConfig({
          mapHide: true
        }),
        pipes: [{
          pipe: this.dataProviders.roundNumericPipe,
          args: [this.dataProviders.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Crop',
        field: nameOf<ITaskManagementPlanBookGridData>('CropName'),
        width: 100,
        cssClass: 'left',
        filterable: true,
        headerFilterable: true
      })
    ];
  }

  /*
   * Columns which are same for all views at the ending of the view
   */
  private _getCommonEndingGridColumnConfig(language: ILanguage): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Nutrients__FertilizationDate',
        field: nameOf<ITaskManagementPlanBookGridData>('ApplDate'),
        format: language.DateFormat,
        hide: new ApDynGridColumnHideConfig({
          mapHide: true
        }),
        width: 100,
        tooltip: (dataItem) => this.dataProviders[this._planSummary.Module].getPlanNumberTooltip(dataItem),
        headerFilterable: true,
        filterable: true
      }),
      new ApDynGridGroupColumnConfig({
        title: 'agrioptions_resources',
        hide: true,
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'Base__Engine',
            field: `${nameOf<ITaskManagementPlanBookGridData>('ExportMachine')}.${nameOf<IMachine>('NameWithSign')}`,
            hide: true,
            width: 100
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'AgriConnect_Ini__Tool',
            field: `${nameOf<ITaskManagementPlanBookGridData>('ExportInstrument')}.${nameOf<IInstrument>('Name')}`,
            hide: true,
            width: 100
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Docu_Ini__Motorist',
            field: `${nameOf<ITaskManagementPlanBookGridData>('ExportDriver')}.${nameOf<IDriver>('ShortFullName')}`,
            hide: true,
            width: 100
          })
        ]
      })
    ];
  }

  /*
   * Columns for : Grunddüngung (Base fertilization)
   */
  private _getBaseFertiGridColumnConfig(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridGroupColumnConfig({
        title: this.dataProviders[this._planSummary.Module].getProductGridHeader(this._planSummary),
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Product',
            field: nameOf<ITaskManagementPlanBookGridData>('ProductName'),
            width: 120,
            headerFilterable: true,
            filterable: true
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Average_Amount',
            field: nameOf<ITaskManagementPlanBookGridData>('ApplRate'),
            tooltip: (dataItem) => dataItem?.ApplRate,
            filterType: FilterType.NUMBER,
            cssClass: 'right',
            width: 60,
            headerFilterable: true,
            filterable: true,
            pipes: [{
              pipe: this.dataProviders.roundNumericPipe,
              args: [this.dataProviders.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Min',
            field: nameOf<ITaskManagementPlanBookGridData>('ApplMin'),
            filterType: FilterType.NUMBER,
            cssClass: 'right',
            hide: new ApDynGridColumnHideConfig({
              mapHide: true
            }),
            width: 60,
            headerFilterable: true,
            filterable: true,
            pipes: [{
              pipe: this.dataProviders.roundNumericPipe,
              args: [this.dataProviders.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Max',
            field: nameOf<ITaskManagementPlanBookGridData>('ApplMax'),
            filterType: FilterType.NUMBER,
            cssClass: 'right',
            hide: new ApDynGridColumnHideConfig({
              mapHide: true
            }),
            width: 60,
            headerFilterable: true,
            filterable: true,
            pipes: [{
              pipe: this.dataProviders.roundNumericPipe,
              args: [this.dataProviders.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
            }]
          })
        ]
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Nutrients_Pages_Popups__Distribution',
        field: nameOf<ITaskManagementPlanBookGridData>('ApplicationModeText'),
        width: 100,
        headerFilterable: true,
        filterable: true
      })
    ];
  }

  /*
   * Columns for burger : Grunddüngung (Base fertilization)
   */
  private _getBaseFertiGridEditColumnConfig(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridEditColumnConfig(
        {
          data: [
            {
              text: 'Global__Edit',
              callback: ApUtilService.createEventEmitter(this._editPlanningBook.bind(this), this._subscriptions),
              disabled: () => true
            },
            {
              text: 'Global__Delete',
              callback: ApUtilService.createEventEmitter(this._deletePlanningBook.bind(this), this._subscriptions)
            },
          ]
        }
      )
    ];
  }

  /*
   * Columns for : ND-N Düngung
   */
  private _getNFertiGridColumnConfig(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Product',
        field: nameOf<ITaskManagementPlanBookGridData>('ProductName'),
        width: 120,
        headerFilterable: true,
        filterable: true
      }),
      new ApDynGridGroupColumnConfig({
        title: this.dataProviders[this._planSummary.Module].getProductGridHeader(this._planSummary),
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'AgriConnect_View__Reference',
            field: nameOf<ITaskManagementPlanBookGridData>('ApplRate'),
            filterType: FilterType.NUMBER,
            cssClass: 'right',
            width: 90,
            headerFilterable: true,
            filterable: true,
            pipes: [{
              pipe: this.dataProviders.roundNumericPipe,
              args: [this.dataProviders.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Min',
            field: nameOf<ITaskManagementPlanBookGridData>('ApplMin'),
            filterType: FilterType.NUMBER,
            cssClass: 'right',
            hide: new ApDynGridColumnHideConfig({
              mapHide: true
            }),
            width: 90,
            headerFilterable: true,
            filterable: true,
            pipes: [{
              pipe: this.dataProviders.roundNumericPipe,
              args: [this.dataProviders.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Max',
            field: nameOf<ITaskManagementPlanBookGridData>('ApplMax'),
            filterType: FilterType.NUMBER,
            cssClass: 'right',
            hide: new ApDynGridColumnHideConfig({
              mapHide: true
            }),
            width: 90,
            headerFilterable: true,
            filterable: true,
            pipes: [{
              pipe: this.dataProviders.roundNumericPipe,
              args: [this.dataProviders.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
            }]
          })
        ]
      })
    ];
  }
}
